import React, { useMemo } from "react";

import {
  CheckCircle,
  Eye,
  MoreVertical,
  PlusCircle,
  Trash2,
} from "lucide-react";
import Swal from "sweetalert2";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useToast } from "~/hooks/use-toast";
import { useOptimizedProfiles } from "~/hooks/useOptimizedProfiles";
import { supabase } from "~/integrations/supabase/client";
import { AddPaymentForm } from "~/pages/purchase-orders/components/order-detail/AddPaymentForm";
import { PaymentRecord } from "~/pages/purchase-orders/hooks/usePayments";
import { useSalesRequests } from "~/pages/purchase-orders/hooks/useSalesRequests";
import { ClientInfo } from "~/pages/purchase-orders/hooks/utils/clientUtils";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";
import {
  isPaymentVerified,
  shouldRecalculatePaidAmount,
} from "~/utils/checkPaymentUtils";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet, resolveGross } from "~/utils/iva";
import { calculateVerifiedPayments } from "~/utils/paymentVerification";

interface PaymentDetailsTableProps {
  payments: PaymentRecord[];
  loading: boolean;
  salesRequest?: SalesRequest;
  onPaymentAdded?: () => Promise<void>;
  clientInfo?: ClientInfo;
  showPaymentForm: boolean;
  setShowPaymentForm: (show: boolean) => void;
}

// Helper function to get status badge - now only handles English status values
const getStatusBadge = (status: string) => {
  switch (status) {
    case "pending":
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          Pendiente
        </Badge>
      );
    case "in_portfolio":
      return (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
          En Cartera
        </Badge>
      );
    case "collected":
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          Cobrado
        </Badge>
      );
    case "paid":
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          Pagado
        </Badge>
      );
    case "protested":
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          Protestado
        </Badge>
      );
    default:
      return null;
  }
};

// Helper function to get status label in Spanish - now only handles English status values
const getStatusLabel = (status: string) => {
  switch (status) {
    case "pending":
      return "Pendiente";
    case "in_portfolio":
      return "En Cartera";
    case "collected":
      return "Cobrado";
    case "paid":
      return "Pagado";
    case "protested":
      return "Protestado";
    default:
      return status;
  }
};

export const PaymentDetailsTable: React.FC<PaymentDetailsTableProps> = ({
  payments,
  loading,
  salesRequest,
  onPaymentAdded = async () => {},
  clientInfo,
  showPaymentForm,
  setShowPaymentForm,
}) => {
  const { toast } = useToast();
  const { getSellerInfo } = useSalesRequests();

  // Get user role to check permissions
  const profileStore = useProfileStore();
  const isTransportista = profileStore?.role === "transportista";
  const isLogistica = profileStore?.role === "logistica";
  const isSeller = profileStore?.role === "seller";
  const canDeletePayments =
    profileStore?.role === "superadmin" || profileStore?.role === "cobranza";

  // Extract all unique user IDs from payments
  const userIds = useMemo(() => {
    const ids = payments
      .map((payment) => payment.createdBy)
      .filter(Boolean) as string[];
    return [...new Set(ids)];
  }, [payments]);

  // Fetch user profiles
  const { getUserName } = useOptimizedProfiles(userIds);

  // Function to view payment proof
  const viewPaymentProof = (imageUrl: string) => {
    window.open(imageUrl, "_blank");
  };

  // Function to update check status
  const updateCheckStatus = async (paymentId: number, newStatus: string) => {
    try {
      // Get the old check status before updating
      const currentPayment = payments.find((p) => p.id === paymentId);
      const oldCheckStatus = currentPayment?.checkStatus;

      // Update both check_status and payment_status when check is collected or paid
      const updateData: any = { check_status: newStatus };
      if (newStatus === "collected" || newStatus === "paid") {
        updateData.payment_status = "completed";
        // Auto-verify check payments when marked as paid
        if (newStatus === "paid") {
          updateData.is_verified = true;
          updateData.verified_at = new Date().toISOString();
          // Note: verified_by would need to be set by auth context if user ID is available
        }
      }

      const { error } = await (supabase as any)
        .from("payments")
        .update(updateData)
        .eq("id", paymentId);

      if (error) throw error;

      // Recalculate paid status when check status change affects payment verification
      // This handles: collected->protested, protested->collected, paid->protested, etc.
      if (
        salesRequest?.id &&
        shouldRecalculatePaidAmount(oldCheckStatus, newStatus)
      ) {
        try {
          // Recalculate paid amount using RPC function that includes collected/paid checks
          const { data: newPaidAmount, error: paidAmountError } =
            await supabase.rpc("get_sales_request_paid_amount", {
              sales_request_id: salesRequest.id,
            });

          if (paidAmountError) {
            console.error("Error calculating paid amount:", paidAmountError);
          } else {
            // Get the sales request to check total amount for paid calculation
            const { data: salesRequestData, error: fetchError } = await supabase
              .from("sales_request")
              .select("price_with_iva, total_amount, is_exento")
              .eq("id", salesRequest.id)
              .single();

            if (fetchError) {
              console.error("Error fetching sales request:", fetchError);
            } else {
              const totalAmount = resolveGross(salesRequestData);
              const isPaid = (newPaidAmount || 0) >= totalAmount;

              // Update the sales request with new paid amount and status
              const { error: updateError } = await supabase
                .from("sales_request")
                .update({
                  paid_amount: newPaidAmount || 0,
                  paid: isPaid,
                })
                .eq("id", salesRequest.id);

              if (updateError) {
                console.error("Error updating sales request:", updateError);
              } else {
                console.log(
                  `Sales request ${salesRequest.id} updated with paid amount: ${newPaidAmount}, paid status: ${isPaid}`
                );
              }
            }
          }
        } catch (updateError) {
          console.error(
            "Error updating sales request paid amount:",
            updateError
          );
        }
      }

      let successMessage = `Estado del cheque actualizado a ${getStatusLabel(newStatus)}`;
      if (newStatus === "collected") {
        successMessage =
          "Cheque cobrado exitosamente. Crédito liberado automáticamente.";
      } else if (newStatus === "in_portfolio") {
        successMessage =
          "Cheque puesto en cartera. Crédito restringido hasta el cobro.";
      } else if (newStatus === "protested") {
        successMessage =
          "Cheque marcado como protestado. Cliente bloqueado para nuevos pedidos.";
      }

      toast({
        title: "Estado Actualizado",
        description: successMessage,
      });

      // Refresh the payments list
      await onPaymentAdded();
    } catch (error) {
      console.error("Error updating check status:", error);
      toast({
        title: "Error",
        description:
          "Error al actualizar el estado del cheque. Inténtalo de nuevo.",
        variant: "destructive",
      });
    }
  };

  // Function to verify a payment
  const handleVerifyPayment = async (paymentId: number) => {
    const result = await Swal.fire({
      title: "¿Verificar pago?",
      text: "¿Estás seguro de que deseas marcar este pago como verificado?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, verificar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const { error } = await supabase
          .from("payments")
          .update({
            is_verified: true,
            verified_at: new Date().toISOString(),
          })
          .eq("id", paymentId);

        if (error) throw error;

        // Verifying a manual payment makes it count toward the debt formula
        // (CxC drops), so the sales_request.paid flag must be recomputed too —
        // otherwise the factura keeps showing "Pendiente" in the sellers'
        // report even though it is fully paid. Mirrors the check path in
        // updateCheckStatus above; coverage matches the CxC/debt formula
        // (price_with_iva - credit_notes - verified_payments).
        if (salesRequest?.id) {
          try {
            const { verifiedAmount, totalPaid } =
              await calculateVerifiedPayments(salesRequest.id);

            const { data: srData } = await supabase
              .from("sales_request")
              .select("price_with_iva, total_amount, is_exento")
              .eq("id", salesRequest.id)
              .single();

            const { data: creditNoteRows } = await supabase
              .from("sales_request_documents")
              .select("total_amount")
              .eq("sales_request_id", salesRequest.id)
              .eq("document_type", "nota_de_credito")
              .is("deleted_at", null);

            const totalWithIva = resolveGross(srData || {});
            const creditNotes = (creditNoteRows || []).reduce(
              (sum, row) => sum + Number(row.total_amount || 0),
              0
            );
            // 1-peso tolerance for rounding, consistent with shouldMarkAsPaid.
            const isPaidInFull =
              Math.round(verifiedAmount) + Math.round(creditNotes) >=
              Math.round(totalWithIva) - 1;

            const { error: updateError } = await supabase
              .from("sales_request")
              .update({
                paid_amount: totalPaid,
                paid: isPaidInFull,
                updated_at: new Date().toISOString(),
              })
              .eq("id", salesRequest.id);

            if (updateError) {
              console.error(
                "Error updating sales request paid status after verification:",
                updateError
              );
            }
          } catch (recalcError) {
            console.error(
              "Error recalculating paid status after verification:",
              recalcError
            );
          }
        }

        toast({
          title: "Pago verificado",
          description: "El pago ha sido marcado como verificado correctamente.",
        });

        // Refresh the payments list
        await onPaymentAdded();
      } catch (error) {
        console.error("Error verifying payment:", error);
        toast({
          title: "Error",
          description: "No se pudo verificar el pago. Inténtalo de nuevo.",
          variant: "destructive",
        });
      }
    }
  };

  // Function to delete a payment
  const handleDeletePayment = async (
    paymentId: number,
    paymentAmount: number
  ) => {
    const result = await Swal.fire({
      title: "¿Eliminar pago?",
      text: `¿Estás seguro de que deseas eliminar este pago de ${formatCurrency(paymentAmount)}? Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const { error } = await supabase
          .from("payments")
          .delete()
          .eq("id", paymentId);

        if (error) throw error;

        // Update sales_request paid_amount after deletion
        if (salesRequest?.id) {
          try {
            // Recalculate paid amount using RPC function
            const { data: newPaidAmount, error: paidAmountError } =
              await supabase.rpc("get_sales_request_paid_amount", {
                sales_request_id: salesRequest.id,
              });

            if (paidAmountError) {
              console.error("Error calculating paid amount:", paidAmountError);
            } else {
              // Get the sales request to check total amount for paid calculation
              const { data: salesRequestData, error: fetchError } =
                await supabase
                  .from("sales_request")
                  .select("price_with_iva, total_amount, is_exento")
                  .eq("id", salesRequest.id)
                  .single();

              if (fetchError) {
                console.error("Error fetching sales request:", fetchError);
              } else {
                const totalAmount = resolveGross(salesRequestData);
                const isPaid = (newPaidAmount || 0) >= totalAmount;

                // Update the sales request with new paid amount and status
                const { error: updateError } = await supabase
                  .from("sales_request")
                  .update({
                    paid_amount: newPaidAmount || 0,
                    paid: isPaid,
                  })
                  .eq("id", salesRequest.id);

                if (updateError) {
                  console.error("Error updating sales request:", updateError);
                }
              }
            }
          } catch (updateError) {
            console.error(
              "Error updating sales request paid amount:",
              updateError
            );
          }
        }

        toast({
          title: "Pago eliminado",
          description: "El pago ha sido eliminado correctamente.",
        });

        // Refresh the payments list
        await onPaymentAdded();
      } catch (error) {
        console.error("Error deleting payment:", error);
        toast({
          title: "Error",
          description: "No se pudo eliminar el pago. Inténtalo de nuevo.",
          variant: "destructive",
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex w-full justify-center p-6">
        <div className="flex animate-pulse space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 w-3/4 rounded bg-gray-200"></div>
            <div className="space-y-2">
              <div className="h-4 rounded bg-gray-200"></div>
              <div className="h-4 w-5/6 rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter out unverified webpay payments (failed payment attempts)
  const filteredPayments = useMemo(() => {
    if (!payments) return [];
    return payments.filter((payment) => {
      // If it's a webpay payment, only show it if it's verified and has a movement ID
      if (payment.paymentMethod === "webpay") {
        return (
          payment.is_verified === true &&
          !!(payment.fintoc_movement_id || payment.rail_movement_id)
        );
      }
      // For non-webpay payments, show them all
      return true;
    });
  }, [payments]);

  // Show payment form if requested (MOVED AFTER ALL HOOKS)
  if (showPaymentForm && salesRequest) {
    return (
      <AddPaymentForm
        selectedOrder={salesRequest}
        onPaymentAdded={async () => {
          await onPaymentAdded();
          setShowPaymentForm(false);
        }}
        totalAmount={grossFromNet(salesRequest.totalAmount, salesRequest.isExento)}
        currentPayments={payments}
      />
    );
  }

  if (!payments || payments.length === 0 || filteredPayments.length === 0) {
    return (
      <div className="p-5 text-center">
        {salesRequest ? (
          <div className="flex flex-col items-center space-y-4">
            <p className="mb-2 text-sm text-muted-foreground">
              No hay pagos registrados
            </p>
            {!isTransportista &&
              !isLogistica &&
              // Sellers can register payments regardless of sales condition
              // For other users, show the button as before
              (!isSeller || (isSeller && salesRequest)) && (
                <Button
                  onClick={() => setShowPaymentForm(true)}
                  variant="outline"
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Registrar pago manual
                </Button>
              )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No hay pagos registrados
          </p>
        )}
      </div>
    );
  }

  // Calculate total amount paid from filtered payments.
  // Only count payments that actually reduce debt: checks must be
  // collected/paid (protested checks are excluded), and non-check
  // payments must be verified. Mirrors get_sales_request_paid_amount().
  const totalPaid = filteredPayments.reduce(
    (sum, payment) =>
      isPaymentVerified(
        payment.paymentMethod,
        payment.is_verified,
        payment.check_status
      )
        ? sum + payment.amount
        : sum,
    0
  );

  return (
    <div className="w-full overflow-auto">
      <table className="w-full caption-bottom divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Fecha
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Monto
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Método
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Banco
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Referencia
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Estado
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Verificación
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Registrado por
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Comprobante
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {filteredPayments.map((payment, index) => {
            const countsTowardTotal = isPaymentVerified(
              payment.paymentMethod,
              payment.is_verified,
              payment.check_status
            );
            return (
              <tr key={index} className="transition-colors hover:bg-gray-50">
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {new Date(payment.createdAt).toLocaleDateString("es-ES", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-900">
                  {formatCurrency(payment.amount)}
                  {!countsTowardTotal && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-gray-200/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600">
                      No suma al total
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    {payment.paymentMethod || "Transferencia"}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {payment.bank || "-"}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {payment.reference || "-"}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {payment.paymentMethod === "check" ? (
                    payment.check_status ? (
                      getStatusBadge(payment.check_status)
                    ) : (
                      <Badge
                        variant="secondary"
                        className="bg-gray-100 text-gray-600"
                      >
                        Sin Estado
                      </Badge>
                    )
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {payment.paymentMethod === "check" ? (
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 text-gray-600"
                    >
                      Ver Estado
                    </Badge>
                  ) : payment.fintoc_movement_id || payment.rail_movement_id ? (
                    <Badge
                      variant="secondary"
                      className="bg-blue-100 text-blue-800"
                    >
                      Por panel
                    </Badge>
                  ) : payment.is_verified === true ? (
                    <Badge
                      variant="secondary"
                      className="bg-green-100 text-green-800"
                    >
                      Verificado
                    </Badge>
                  ) : payment.is_verified === false ? (
                    <Badge
                      variant="secondary"
                      className="bg-yellow-100 text-yellow-800"
                    >
                      Pendiente
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 text-gray-600"
                    >
                      N/A
                    </Badge>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {payment.createdBy ? (
                    <span className="text-xs font-medium text-blue-600">
                      {getUserName(payment.createdBy)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">Sistema</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {payment.image_url ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => viewPaymentProof(payment.image_url)}
                      className="h-8 px-3 text-blue-600 hover:text-blue-800"
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Ver
                    </Button>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {canDeletePayments ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-gray-600 hover:bg-gray-100"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleVerifyPayment(payment.id)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Verificar Pago
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleDeletePayment(payment.id, payment.amount)
                          }
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Eliminar Pago
                        </DropdownMenuItem>
                        {payment.paymentMethod === "check" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>
                              Cambiar Estado del Cheque
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(payment.id, "pending")
                              }
                              disabled={payment.check_status === "pending"}
                            >
                              Marcar como Pendiente
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(payment.id, "in_portfolio")
                              }
                              disabled={payment.check_status === "in_portfolio"}
                            >
                              Marcar como En Cartera
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(payment.id, "collected")
                              }
                              disabled={payment.check_status === "collected"}
                            >
                              Marcar como Cobrado
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(payment.id, "paid")
                              }
                              disabled={payment.check_status === "paid"}
                            >
                              Marcar como Pagado
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(payment.id, "protested")
                              }
                              disabled={payment.check_status === "protested"}
                              className="text-red-600 hover:text-red-700"
                            >
                              Marcar como Protestado
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            );
          })}
          <tr className="bg-gray-50">
            <td
              colSpan={3}
              className="px-4 py-3 text-sm font-medium text-gray-700"
            >
              Total pagado (verificado):
            </td>
            <td
              colSpan={6}
              className="px-4 py-3 text-sm font-bold text-gray-900"
            >
              {formatCurrency(totalPaid)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
